import { _GettersTree, defineStore } from 'pinia';

import { AbstractBaseStore, AbstractStoreState } from "@/stores/AbstractBase.store";
import { OrderDirection, OrderParameter } from '@/models/OrderParameter.model';
import { EvaluationLevelFilterDef } from './filters/EvaluationLevelFilterDef';
import { Evaluation } from '@/models/Evaluation.model';
import { EvaluationFilterDef } from './filters/EvaluationFilterDef';
import { useObservationsStore } from './Observations.store';
import { Observation } from '@/models/Observation.model';
import { Goal } from '@/models/Goal.model';
import { usePupilActivitiesStore } from './PupilActivities.store';
import { useLessonsStore } from './Lessons.store';
import { Lesson } from '@/models/Lesson.model';
import { useGoalsStore } from "@/stores/Goals.store.ts";

interface EvaluationsStoreState extends AbstractStoreState<Evaluation, EvaluationFilterDef> {

}

const BaseStore = AbstractBaseStore<Evaluation, EvaluationsStoreState, EvaluationFilterDef>();

/**
 * Observation store.
 */
export const useEvaluationsStore = defineStore('evaluations', {


	/**
	 * Return the initial state of the store.
	 */
	state: (): EvaluationsStoreState => {

		// Base state from the AbstractBaseStore
		return BaseStore.state({

			order: new OrderParameter('position', OrderDirection.DESC),
			defaultFilter: new EvaluationLevelFilterDef([]),

		});

	},

	getters: {

		...BaseStore.getters,

		apiPath(): string {
			return 'evaluations';
		},

		defaultMask() {
			return [
				'*',
				'evaluationLevel.*',
				'pupil.id',
				'goal.id',
				'author.*',
				'observation.*',
			];
		},

		defaultRecordsPerPage() {
			return 1000;
		},

	} as _GettersTree<EvaluationsStoreState>,

	actions: {

		...BaseStore.actions,

		mapToItem(data: any): Evaluation {
			return Evaluation.createFromApiData(data);
		},

		new(): Evaluation {
			
			const evaluation = new Evaluation();
			evaluation.comment = '';
			evaluation.date = new Date();
			evaluation.evaluationLevel = undefined;
			evaluation.pupilId = undefined;
			evaluation.goalId = undefined;
			return evaluation;

		},

		/**
		 * Create many evaluations in one X-Bulk request. Every model is hydrated from the
		 * response and afterSave runs per item, so loaded goals pick up the new evaluations.
		 * Throws an ApiErrorMessageBag on failure (the backend saves all-or-nothing).
		 */
		async bulkCreate(evaluations: Evaluation[]): Promise<Evaluation[]> {
			return await this.superBulkSave(evaluations);
		},

		async afterSave(model: Evaluation) {

			if (!model.goalId || !model.pupilId) {
				return;
			}

			// Check if we have any goals loaded in other stores.
			const goalsStore = useGoalsStore();
			const goal = goalsStore.find(model.goalId);
			
			if (goal) {
				goal.addEvaluation(model.pupilId, model);
			}

			// Also check if we have any observations with this goal loaded
			this._findGoalsInObservationStore(model).forEach((goal: Goal) => {
				goal.addEvaluation(model.pupilId, model);
			});

			this._findGoalsInPupilActivityStore(model).forEach((goal: Goal) => {
				goal.addEvaluation(model.pupilId, model);
			});

			this._findGoalsInLessonsStore(model).forEach((goal: Goal) => {
				goal.addEvaluation(model.pupilId, model);
			});
		},

		async afterDelete(model: Evaluation) {
			
			if (!model.goalId || !model.pupilId) {
				return;
			}

			// Look for the goal in the goals insight store
			const goalsStore = useGoalsStore();
			const goal = goalsStore.find(model.goalId);

			if (goal) {
				goal.removeEvaluation(model.pupilId, model);
			}

			// Also check if we have any observations with this goal loaded
			this._findGoalsInObservationStore(model).forEach((goal: Goal) => {
				goal.removeEvaluation(model.pupilId, model);
			});

			this._findGoalsInPupilActivityStore(model).forEach((goal: Goal) => {
				goal.removeEvaluation(model.pupilId, model);
			});

			this._findGoalsInLessonsStore(model).forEach((goal: Goal) => {
				goal.removeEvaluation(model.pupilId, model);
			});

		},

		_findGoalsInObservationStore(model: Evaluation) {

			const observationsStore = useObservationsStore();
			const goals: Goal[] = [];
			
			observationsStore.items.forEach((observation: Observation) => {
				if (observation.pupilActivity?.lessonItem?.lessonPlan?.goals?.length) {
					observation.pupilActivity.lessonItem.lessonPlan.goals.forEach((goal: Goal) => {
						if (goal.id === model.goalId) {
							goals.push(goal);
						}
					});
				}
			});

			return goals;

		},

		_findGoalsInPupilActivityStore(model: Evaluation) {

			const pupilActivityStore = usePupilActivitiesStore();
			return pupilActivityStore.loadedGoals.filter((goal: Goal) => {
				return goal.id === model.goalId;
			});

		},

		_findGoalsInLessonsStore(model: Evaluation) {

			const lessonsStore = useLessonsStore();

			const goals: Goal[] = [];

			lessonsStore.events.forEach((lesson: Lesson) => {
				lesson.lessonItems.forEach((lessonItem) => {
					if (lessonItem.lessonPlan?.goals?.length) {
						lessonItem.lessonPlan.goals.forEach((goal: Goal) => {
							if (goal.id === model.goalId) {
								goals.push(goal);
							}
						});
					}
				});
			});

			return goals;
		}
		
	}

});
